import React from 'react';
import { Product } from '../../types';
import { useInView } from '../../hooks/useInView';
import { lookupHeritage } from '../../data/manufacturerHeritage';
import { lookupBenchmarkForProduct, ourCheapestPerUnit, PriceBenchmark } from '../../data/priceBenchmarks';
import {
  parseDescription,
  sortSections,
  parseEffectsTiming,
  parseMetadataPairs,
  splitLeadAndBullets,
  ParsedSection,
} from '../../utils/parseDescription';

// ══════════════════════════════════════════════════════════════════════
// ManufacturerSeal: typographic "wax seal" badge rendered from heritage
// data (no images, no trademark risk). Two visual modes:
//   - full: big vertical card for a dedicated callout
//   - inline: compact pill for use inline with the specs row
// ══════════════════════════════════════════════════════════════════════
interface ManufacturerSealProps {
  manufacturer: string;
  mode?: 'full' | 'inline';
}
const ManufacturerSeal: React.FC<ManufacturerSealProps> = ({ manufacturer, mode = 'full' }) => {
  const heritage = lookupHeritage(manufacturer);
  // Strip trailing corporate suffixes for the display wordmark
  const display = manufacturer.replace(
    /\s*(Pvt\.?\s*Ltd\.?|Private\s*Limited|Ltd\.?|Limited|Inc\.?|Corporation|Corp\.?|Co\.?)\s*$/gi,
    ''
  ).trim();

  if (mode === 'inline') {
    return (
      <div className="inline-flex items-baseline gap-2 px-3 py-1 border border-gold-400/50 bg-cream-50/50 rounded-sm">
        <span className="font-display italic text-[13px] text-gold-600">❦</span>
        <span className="font-display text-[14px] text-forest-900 tracking-wide">{display}</span>
      </div>
    );
  }

  return (
    <div className="relative inline-flex flex-col items-center px-10 py-6 min-w-[260px]">
      {/* Decorative double border — evokes a certificate / wax seal */}
      <div className="absolute inset-0 border-[1.5px] border-gold-400/70 rounded-sm" aria-hidden="true" />
      <div className="absolute inset-[3px] border border-gold-400/30 rounded-[1px]" aria-hidden="true" />

      {/* Top flourish */}
      <div className="flex items-center gap-2 text-gold-600 mb-2" aria-hidden="true">
        <span className="text-[10px] tracking-[0.3em] font-accent italic">— manufactured by —</span>
      </div>

      {/* Wordmark */}
      <div className="font-display text-[22px] sm:text-[26px] text-forest-900 leading-none tracking-tight whitespace-nowrap">
        {display}
      </div>

      {/* Bottom heritage line */}
      {heritage ? (
        <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-warm-500 font-accent italic">
          <span>Est.&nbsp;{heritage.founded}</span>
          <span className="w-1 h-1 rounded-full bg-gold-500" aria-hidden="true" />
          <span>{heritage.city}, {heritage.country}</span>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-warm-500 font-accent italic">
          <span className="w-6 h-px bg-gold-400/60" aria-hidden="true" />
          <span>Pharmaceutical Manufacturer</span>
          <span className="w-6 h-px bg-gold-400/60" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Reveal: fade-up + slight translate when scrolled into view
// ══════════════════════════════════════════════════════════════════════
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.12 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Inline markdown: **bold**, _italic_, plain text
// ══════════════════════════════════════════════════════════════════════
const inlineMarkdown = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-forest-900">$1</strong>')
    .replace(/(^|[\s(])_([^_]+?)_(?=[\s.,)!?]|$)/g, '$1<em class="italic text-warm-700">$2</em>');

interface BlockRenderProps {
  content: string;
  className?: string;
}

// Render markdown paragraphs + bullets without heavy external deps
const MarkdownBody: React.FC<BlockRenderProps> = ({ content, className = '' }) => {
  const lines = content.split('\n');
  type Block =
    | { type: 'p'; text: string }
    | { type: 'ul'; items: string[] };
  const blocks: Block[] = [];
  let current: Block | null = null;
  for (const line of lines) {
    if (line.startsWith('- ')) {
      if (!current || current.type !== 'ul') {
        if (current) blocks.push(current);
        current = { type: 'ul', items: [] };
      }
      current.items.push(line.slice(2).trim());
    } else if (line.trim() === '') {
      if (current) { blocks.push(current); current = null; }
    } else {
      if (!current || current.type !== 'p') {
        if (current) blocks.push(current);
        current = { type: 'p', text: '' };
      }
      current.text += (current.text ? ' ' : '') + line.trim();
    }
  }
  if (current) blocks.push(current);

  return (
    <div className={className}>
      {blocks.map((b, i) =>
        b.type === 'p' ? (
          <p
            key={i}
            className="mb-4 last:mb-0 leading-[1.75]"
            dangerouslySetInnerHTML={{ __html: inlineMarkdown(b.text) }}
          />
        ) : (
          <ul key={i} className="space-y-2 mb-4 last:mb-0">
            {b.items.map((item, j) => (
              <li key={j} className="flex items-start gap-3">
                <span className="mt-[0.65em] w-[5px] h-[5px] rounded-full bg-gold-500 flex-shrink-0" />
                <span
                  className="leading-[1.65]"
                  dangerouslySetInnerHTML={{ __html: inlineMarkdown(item) }}
                />
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Roman numeral helper (1 → I, 2 → II, ...)
// ══════════════════════════════════════════════════════════════════════
const roman = (n: number): string => {
  const r: [number, string][] = [[10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let out = '';
  for (const [v, s] of r) {
    while (n >= v) { out += s; n -= v; }
  }
  return out;
};

// ══════════════════════════════════════════════════════════════════════
// Shared header: roman numeral gutter + eyebrow + display heading
// ══════════════════════════════════════════════════════════════════════
interface SectionHeaderProps {
  index: number;
  eyebrow: string;
  title: string;
  variant?: 'default' | 'warning';
}
const SectionHeader: React.FC<SectionHeaderProps> = ({ index, eyebrow, title, variant = 'default' }) => {
  const eyebrowColor = variant === 'warning' ? 'text-clay-600' : 'text-gold-700';
  const titleColor = variant === 'warning' ? 'text-clay-600' : 'text-forest-900';
  return (
    <div className="mb-7">
      <div className="flex items-baseline gap-4 mb-2">
        <span
          className={`font-accent italic text-xl md:text-2xl ${eyebrowColor} leading-none select-none`}
          aria-hidden="true"
        >
          {roman(index)}.
        </span>
        <span className={`text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-accent italic ${eyebrowColor}`}>
          §&nbsp;{eyebrow}
        </span>
      </div>
      <h3 className={`font-display text-[28px] sm:text-[34px] md:text-[40px] ${titleColor} leading-[1.05] tracking-tight`}>
        {title}
      </h3>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Decorative section divider: thin rules + centered gold marker
// ══════════════════════════════════════════════════════════════════════
const SectionDivider: React.FC = () => (
  <div className="flex items-center justify-center my-16" aria-hidden="true">
    <div className="h-px bg-warm-200 flex-1 max-w-[110px]" />
    <div className="mx-5 flex items-center gap-2">
      <div className="w-1 h-1 rounded-full bg-gold-400/70" />
      <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
      <div className="w-1 h-1 rounded-full bg-gold-400/70" />
    </div>
    <div className="h-px bg-warm-200 flex-1 max-w-[110px]" />
  </div>
);

// ══════════════════════════════════════════════════════════════════════
// Section I: Overview — large editorial lead paragraph
// ══════════════════════════════════════════════════════════════════════
const OverviewSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => (
  <section className="relative py-2">
    <SectionHeader index={index} eyebrow="Overview" title="Product Overview" />
    <div className="relative">
      <div className="absolute -left-3 top-0 bottom-0 w-[2px] bg-gradient-to-b from-gold-400/0 via-gold-500/60 to-gold-400/0 hidden sm:block" />
      <MarkdownBody
        content={section.content}
        className="font-accent text-[17px] md:text-[19px] text-warm-800 leading-[1.75] max-w-[62ch]"
      />
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// Section II: Mechanism of Action — forest-tinted card with left border
// ══════════════════════════════════════════════════════════════════════
const MechanismSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => (
  <section className="relative py-2">
    <SectionHeader index={index} eyebrow="Mechanism of Action" title="How It Works" />
    <div className="relative bg-forest-50/70 border border-forest-200/80 rounded-lg overflow-hidden">
      {/* Faint botanical left gradient */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-forest-400 via-forest-500 to-forest-600" />
      {/* Subtle leaf-like decoration */}
      <svg
        className="absolute -right-6 -top-6 w-40 h-40 text-forest-200/40 pointer-events-none"
        viewBox="0 0 100 100"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        aria-hidden="true"
      >
        <path d="M50 10 Q70 30 80 50 Q75 75 50 90 Q25 75 20 50 Q30 30 50 10 Z" />
        <path d="M50 10 L50 90" />
        <path d="M50 30 Q60 35 65 45" />
        <path d="M50 45 Q62 50 70 60" />
        <path d="M50 60 Q60 65 65 75" />
        <path d="M50 30 Q40 35 35 45" />
        <path d="M50 45 Q38 50 30 60" />
        <path d="M50 60 Q40 65 35 75" />
      </svg>
      <div className="relative px-7 py-7 pl-9">
        <MarkdownBody
          content={section.content}
          className="text-[15px] md:text-base text-warm-800 leading-[1.8]"
        />
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// Section III: Effects — two-column timing card with gold diamond link
// ══════════════════════════════════════════════════════════════════════
const EffectsSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => {
  const { onset, duration } = parseEffectsTiming(section.content);
  return (
    <section className="relative py-2">
      <SectionHeader index={index} eyebrow="Timing" title="Effects & Duration" />
      <div className="relative bg-gradient-to-br from-cream-50 via-cream-100 to-cream-50 border border-warm-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-2 relative">
          {/* Onset */}
          <div className="px-6 sm:px-10 py-10 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold-700 font-accent italic mb-4">
              — Onset —
            </div>
            <div className="font-display text-[24px] sm:text-[30px] text-forest-900 leading-[1.1] tracking-tight">
              {onset || '—'}
            </div>
            <div className="mt-3 text-[11px] text-warm-500 italic font-accent">
              time until effect begins
            </div>
          </div>

          {/* Center vertical rule */}
          <div className="absolute left-1/2 top-10 bottom-10 w-px bg-warm-300" />

          {/* Gold diamond marker overlay */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="w-3 h-3 bg-gold-500 rotate-45 shadow-[0_0_0_4px_rgb(251,248,240)]" />
          </div>

          {/* Duration */}
          <div className="px-6 sm:px-10 py-10 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold-700 font-accent italic mb-4">
              — Duration —
            </div>
            <div className="font-display text-[24px] sm:text-[30px] text-forest-900 leading-[1.1] tracking-tight">
              {duration || '—'}
            </div>
            <div className="mt-3 text-[11px] text-warm-500 italic font-accent">
              length of effect
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Price Comparison Card — brand-name retail vs our generic
// Sits between Effects and Dosage sections when benchmark data exists
// ══════════════════════════════════════════════════════════════════════
interface PriceComparisonCardProps {
  benchmark: PriceBenchmark;
  ourPrice: number; // cheapest per-item rate
  productBrand: string;
}
const PriceComparisonCard: React.FC<PriceComparisonCardProps> = ({
  benchmark,
  ourPrice,
  productBrand,
}) => {
  // Don't render if our price is 0 or higher than brand (something's wrong)
  if (ourPrice <= 0 || ourPrice >= benchmark.retailPrice) return null;

  const saved = benchmark.retailPrice - ourPrice;
  const savedPct = Math.round((saved / benchmark.retailPrice) * 100);
  // Multiplier framing (e.g., "23× cheaper") lands harder than a percentage
  // when the ratio is large.
  const multiple = benchmark.retailPrice / ourPrice;
  const showMultiplier = multiple >= 3;

  // Money formatter with 2 decimals for dollar-level precision
  const fmt = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <section className="relative py-2">
      <div className="flex items-baseline gap-4 mb-2">
        <span
          className="font-accent italic text-xl md:text-2xl text-gold-700 leading-none select-none"
          aria-hidden="true"
        >
          ◆
        </span>
        <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-accent italic text-gold-700">
          §&nbsp;Value Comparison
        </span>
      </div>
      <h3 className="font-display text-[28px] sm:text-[34px] md:text-[40px] text-forest-900 leading-[1.05] tracking-tight mb-7">
        The Savings Story
      </h3>

      <div className="relative bg-forest-900 rounded-lg overflow-hidden text-cream-100">
        {/* Ornamental gold border inset */}
        <div className="absolute inset-[5px] border border-gold-500/30 rounded-[4px] pointer-events-none" aria-hidden="true" />
        {/* Subtle grid pattern texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgb(197,165,114) 1px, transparent 1px), linear-gradient(90deg, rgb(197,165,114) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
          aria-hidden="true"
        />

        <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-0 p-8 md:p-10 items-center">
          {/* LEFT — US retail: generic when available (labelled "Generic X"),
              brand name when no US generic exists. */}
          <div className="text-center md:text-right md:pr-10">
            <div className="text-[9px] uppercase tracking-[0.3em] text-gold-400/80 font-accent italic mb-3">
              — US Retail —
            </div>
            <div className="font-display text-[22px] sm:text-[26px] text-cream-200 leading-[1.1] mb-1">
              {benchmark.isBrandBaseline ? benchmark.usaBrand : `Generic ${benchmark.usaBrand}`}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cream-400/60 font-accent italic mb-4">
              {benchmark.isBrandBaseline ? `by ${benchmark.manufacturer}` : 'chain pharmacy, cash'}
            </div>
            <div className="font-display text-[44px] sm:text-[52px] text-cream-200/90 leading-none tracking-tight">
              ${fmt(benchmark.retailPrice)}
            </div>
            <div className="text-[11px] text-cream-400/70 italic mt-1">
              per {benchmark.unit} · {benchmark.dosage}
            </div>
          </div>

          {/* CENTER — Divider with diamond and arrow */}
          <div className="hidden md:flex flex-col items-center justify-center px-2">
            <div className="w-px h-10 bg-gold-500/40" />
            <div className="relative my-2">
              <div className="w-5 h-5 bg-gold-500 rotate-45 shadow-[0_0_0_4px_rgb(20,43,33)]" />
              <div className="absolute inset-0 flex items-center justify-center -rotate-45">
                <svg className="w-3 h-3 text-forest-900" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <div className="w-px h-10 bg-gold-500/40" />
          </div>

          {/* Mobile center divider */}
          <div className="flex items-center justify-center md:hidden py-3">
            <div className="h-px bg-gold-500/40 w-20" />
            <div className="mx-3 w-4 h-4 bg-gold-500 rotate-45" />
            <div className="h-px bg-gold-500/40 w-20" />
          </div>

          {/* RIGHT — Our price */}
          <div className="text-center md:text-left md:pl-10">
            <div className="text-[9px] uppercase tracking-[0.3em] text-gold-400/80 font-accent italic mb-3">
              — Our Generic —
            </div>
            <div className="font-display text-[22px] sm:text-[26px] text-cream-100 leading-[1.1] mb-1">
              {productBrand}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cream-400/60 font-accent italic mb-4">
              same active ingredient
            </div>
            <div className="font-display text-[44px] sm:text-[52px] text-gold-400 leading-none tracking-tight">
              ${fmt(ourPrice)}
            </div>
            <div className="text-[11px] text-cream-400/70 italic mt-1">
              per {benchmark.unit} · bulk price
            </div>
          </div>
        </div>

        {/* Bottom — Savings callout band */}
        <div className="relative border-t border-gold-500/30 px-8 md:px-10 py-5 bg-forest-950/40 flex items-center justify-center gap-4 flex-wrap">
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-[0.25em] text-cream-400/70 font-accent italic">
              You save
            </span>
            <span className="font-display text-[22px] text-gold-400 tracking-tight">
              ${fmt(saved)}
            </span>
            <span className="text-[10px] text-cream-400/60 font-accent italic">
              per {benchmark.unit}
            </span>
          </div>
          <div className="w-px h-5 bg-gold-500/30" aria-hidden="true" />
          {showMultiplier ? (
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-cream-400/70 font-accent italic">
                That is
              </span>
              <span className="font-display text-[22px] text-gold-400 tracking-tight">
                {multiple.toFixed(multiple < 10 ? 1 : 0)}× cheaper
              </span>
            </div>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-cream-400/70 font-accent italic">
                A discount of
              </span>
              <span className="font-display text-[22px] text-gold-400 tracking-tight">
                {savedPct}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Source attribution */}
      <div className="mt-3 text-center text-[10px] text-warm-400 italic font-accent">
        Reference: {benchmark.source}. Our products contain the same active
        ingredient ({benchmark.dosage}), manufactured under WHO-GMP standards in India.
        Savings vary with discount programs and local pharmacy pricing.
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Section IV: Dosage — prescription-paper card with ℞ monogram
// ══════════════════════════════════════════════════════════════════════
const DosageSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => (
  <section className="relative py-2">
    <SectionHeader index={index} eyebrow="Administration" title="Dosage" />
    <div className="relative bg-cream-50 border border-gold-300/60 rounded-sm overflow-hidden"
         style={{ boxShadow: '0 1px 0 rgba(197,165,114,0.3), 0 6px 22px -8px rgba(30,58,45,0.12)' }}>
      {/* ruled paper texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 27px, #1B3A2D 27px, #1B3A2D 28px)',
        }}
        aria-hidden="true"
      />
      {/* Rx monogram (unicode U+211E) */}
      <div
        className="absolute top-5 right-6 font-accent italic text-[88px] text-gold-300/50 leading-none select-none pointer-events-none"
        aria-hidden="true"
      >
        ℞
      </div>
      <div className="relative px-8 py-9 pr-24">
        <div className="text-[10px] uppercase tracking-[0.25em] text-gold-700 font-accent italic mb-4">
          Prescribed instructions
        </div>
        <MarkdownBody
          content={section.content}
          className="font-accent text-[16px] md:text-[17px] text-warm-800 leading-[1.8]"
        />
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// Section V: Side Effects — numbered grid with hairline dividers
// ══════════════════════════════════════════════════════════════════════
const SideEffectsSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => {
  const { lead, bullets } = splitLeadAndBullets(section.content);
  return (
    <section className="relative py-2">
      <SectionHeader index={index} eyebrow="Common Effects" title="Side Effects" />
      {lead && (
        <p className="mb-5 text-[15px] text-warm-700 leading-[1.75] max-w-[58ch]">
          {lead.replace(/\*\*(.+?)\*\*/g, '$1').replace(/_(.+?)_/g, '$1')}
        </p>
      )}
      {bullets.length > 0 && (
        <div className="border-t border-b border-warm-200">
          <div className="grid grid-cols-1 sm:grid-cols-2">
            {bullets.map((item, i) => (
              <div
                key={i}
                className={`flex items-baseline gap-4 py-3.5 px-1 ${
                  i < bullets.length - 1 && i < bullets.length - (bullets.length % 2 === 0 ? 2 : 1) ? 'border-b border-warm-200/60' : ''
                } ${i % 2 === 0 ? 'sm:border-r sm:border-warm-200/60 sm:pr-5' : 'sm:pl-5'}`}
              >
                <span className="font-mono text-[10px] text-gold-600 tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  className="text-[14px] text-warm-700 leading-snug flex-1"
                  dangerouslySetInnerHTML={{ __html: inlineMarkdown(item) }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Section VI: Precautions — clay-toned advisory with shield icon
// ══════════════════════════════════════════════════════════════════════
const PrecautionsSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => (
  <section className="relative py-2">
    <SectionHeader index={index} eyebrow="Advisory" title="Precautions" variant="warning" />
    <div className="relative bg-clay-400/[0.08] border border-clay-400/40 rounded-lg overflow-hidden">
      <div className="absolute inset-y-0 left-0 w-[3px] bg-clay-600" />
      <div className="absolute top-6 right-6 text-clay-500/70" aria-hidden="true">
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4" />
          <circle cx="12" cy="15" r="0.6" fill="currentColor" />
        </svg>
      </div>
      <div className="relative px-7 py-6 pl-9 pr-16">
        <MarkdownBody
          content={section.content}
          className="text-[14px] md:text-[15px] text-warm-800 leading-[1.75]"
        />
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// Section VII: Storage — calm cream card with archive icon
// ══════════════════════════════════════════════════════════════════════
const StorageSection: React.FC<{ content: string; index: number }> = ({ content, index }) => (
  <section className="relative py-2">
    <SectionHeader index={index} eyebrow="Preservation" title="Storage" />
    <div className="relative bg-cream-50 border border-warm-200 rounded-lg overflow-hidden">
      <div className="absolute top-6 right-6 text-forest-400/60" aria-hidden="true">
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25">
          <rect x="3" y="5" width="18" height="4" rx="1" />
          <path d="M5 9v10a1 1 0 001 1h12a1 1 0 001-1V9" />
          <path d="M10 13h4" />
        </svg>
      </div>
      <div className="relative px-7 py-6 pr-16">
        <MarkdownBody
          content={content}
          className="text-[14px] md:text-[15px] text-warm-700 leading-[1.75]"
        />
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// Section VIII: Metadata — letterpress specification table
// ══════════════════════════════════════════════════════════════════════
const MetadataSection: React.FC<{ section: ParsedSection; index: number }> = ({ section, index }) => {
  const pairs = parseMetadataPairs(section.content);
  if (pairs.length === 0) return null;

  // Separate manufacturer from other pairs so we can render it as a branded seal
  const mfrPair = pairs.find(p => /manufacturer/i.test(p.key));
  const otherPairs = pairs.filter(p => !/manufacturer/i.test(p.key));

  return (
    <section className="relative py-2">
      <SectionHeader index={index} eyebrow="Product Information" title="Specifications" />

      {mfrPair && (
        <div className="flex justify-center mb-10">
          <ManufacturerSeal manufacturer={mfrPair.value} mode="full" />
        </div>
      )}

      <div className="relative">
        <div className="border-t-[1.5px] border-b-[1.5px] border-gold-400/50">
          {otherPairs.map(({ key, value }, i) => (
            <div
              key={i}
              className={`grid grid-cols-[minmax(120px,180px)_1fr] gap-4 sm:gap-8 py-3.5 px-1 ${
                i < otherPairs.length - 1 ? 'border-b border-warm-200/50' : ''
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold-700 font-accent italic pt-[3px]">
                {key}
              </div>
              <div className="text-[15px] text-forest-900 font-medium leading-snug">
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Closing disclaimer — italic serif footer note
// ══════════════════════════════════════════════════════════════════════
const DisclaimerBlock: React.FC<{ content: string }> = ({ content }) => (
  <div className="pt-12 mt-2">
    <div className="max-w-[56ch] mx-auto text-center">
      <div className="flex items-center justify-center gap-3 mb-5" aria-hidden="true">
        <div className="h-px bg-warm-300 w-10" />
        <span className="text-gold-500 text-sm">✦</span>
        <div className="h-px bg-warm-300 w-10" />
      </div>
      <p className="font-accent italic text-[13px] sm:text-[14px] text-warm-500 leading-[1.75]">
        {content}
      </p>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════
// Main component: parse + dispatch sections
// ══════════════════════════════════════════════════════════════════════
interface ProductDescriptionProps {
  product: Product;
}

const ProductDescription: React.FC<ProductDescriptionProps> = ({ product }) => {
  const sections = React.useMemo(() => {
    const parsed = parseDescription(product.description || '');
    // Inject synthetic storage section from structured storage_instructions field
    const hasStorage = parsed.some(s => s.id === 'storage');
    if (!hasStorage && product.storage_instructions && product.storage_instructions.trim()) {
      parsed.push({
        id: 'storage',
        title: 'Storage',
        content: product.storage_instructions.trim(),
      });
    }
    return sortSections(parsed);
  }, [product.description, product.storage_instructions]);

  // Price benchmark — product-aware lookup. Detects pen form and
  // uses Ozempic/Wegovy benchmarks instead of Rybelsus for semaglutide pens.
  const benchmark = React.useMemo(() => {
    return lookupBenchmarkForProduct(product as any);
  }, [product]);

  const ourPerUnit = React.useMemo(() => {
    const defaultVariant = (product as any).variants?.find((v: any) => v.is_default)
      || (product as any).variants?.[0];
    const prices = defaultVariant?.prices || [];
    return ourCheapestPerUnit(prices, (product as any).pill_quantity || 0);
  }, [product]);

  const productBrand = React.useMemo(() => {
    const name = product.name || '';
    const dash = name.indexOf(' - ');
    const after = dash >= 0 ? name.substring(dash + 3) : name;
    return after.replace(/\s*\([^)]+\)\s*$/, '').trim();
  }, [product.name]);

  if (sections.length === 0) return null;

  // Split content sections from disclaimer so disclaimer renders as a footer
  const contentSections = sections.filter(s => s.id !== 'disclaimer');
  const disclaimer = sections.find(s => s.id === 'disclaimer');

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      <Reveal className="text-center mb-14">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-gold-700 font-accent italic mb-3">
          — Product Monograph —
        </div>
        <h2 className="font-display text-[36px] sm:text-[44px] md:text-[56px] text-forest-900 leading-[1.0] tracking-tight">
          Product Details
        </h2>
      </Reveal>

      {contentSections.map((section, idx) => {
        const index = idx + 1;
        const node = (() => {
          switch (section.id) {
            case 'overview':    return <OverviewSection section={section} index={index} />;
            case 'mechanism':   return <MechanismSection section={section} index={index} />;
            case 'effects':     return <EffectsSection section={section} index={index} />;
            case 'dosage':      return <DosageSection section={section} index={index} />;
            case 'sideEffects': return <SideEffectsSection section={section} index={index} />;
            case 'precautions': return <PrecautionsSection section={section} index={index} />;
            case 'storage':     return <StorageSection content={section.content} index={index} />;
            case 'metadata':    return <MetadataSection section={section} index={index} />;
            default:            return null;
          }
        })();
        if (!node) return null;

        // Insert price comparison after Effects section (if benchmark data exists)
        const showBenchmarkAfter = section.id === 'effects' && benchmark && ourPerUnit > 0 && ourPerUnit < benchmark.retailPrice;

        return (
          <React.Fragment key={section.id + idx}>
            {idx > 0 && (
              <Reveal delay={40}>
                <SectionDivider />
              </Reveal>
            )}
            <Reveal delay={idx === 0 ? 0 : 80}>{node}</Reveal>
            {showBenchmarkAfter && benchmark && (
              <>
                <Reveal delay={40}>
                  <SectionDivider />
                </Reveal>
                <Reveal delay={80}>
                  <PriceComparisonCard
                    benchmark={benchmark}
                    ourPrice={ourPerUnit}
                    productBrand={productBrand}
                  />
                </Reveal>
              </>
            )}
          </React.Fragment>
        );
      })}

      {disclaimer && (
        <Reveal delay={120}>
          <DisclaimerBlock content={disclaimer.content} />
        </Reveal>
      )}
    </div>
  );
};

export default ProductDescription;
